import {
  FluidButtonConfig,
  FluidIconKey,
  FluidRowData,
  FluidSize,
  FluidSortOrder,
  FluidTableActionColumnConfig,
  FluidTableColumnGroup,
  FluidTableGroupedDataset,
  FluidTableHeader,
  FluidTableSelectionOptions,
  FluidTheme,
  HTMLFluidTableElement,
} from '@lmig/fluid-core';
import { EventEmitter, h, VNode } from '@stencil/core';

import {
  DeleteIcon,
  InfoPopover,
  SortIcon,
} from '../../../common/fluid-common-icon.components';
import { FluidComponentFromDefinition } from '../../../common/fluid-component-rendering.components';
import {
  EXPANDED,
  FORM_EXPANDED,
  GROUP_EXPANDED,
  TABLE_CELL_ID,
  TABLE_HEADER_CELL_ID,
} from '../../../constants/constants';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { EventUtils } from '../../../utils/events/event-utils';
import { extractIconPopoverConfig } from '../../../utils/mapping/mapping.utils';
import { themeVar } from '../../../utils/themes/theme.variables';
import { Utils } from '../../../utils/utils';
import { FluidTable } from '../fluid-table';
import {
  collectGroupCollapsedState,
  isAdditionalHeaderTable,
  isGroupedTable,
} from '../internal/fluid-table-grouping.utils';
import { getSelectColumnWidth } from '../internal/fluid-table-select.utils';
import { sortRowsBasedOnExpandButtonPosition } from '../internal/fluid-table-sort.utils';
import {
  getFixedWidthStyle,
  handleCellClick,
  handleColumnVisibilityChange,
} from '../internal/fluid-table-structure.utils';
import { getCellAlignment } from '../internal/fluid-table-templating.utils';
import { TableDisplayStateStore } from '../stores/table-display-state.store';
import { TableCell } from './fluid-table-cell.components';
import {
  AddSelectHeader,
  ColumnFilter,
  Filter,
  TableHeaderCellProps,
} from './fluid-table-header-cell.components';

// ================================================================ //
// -- SlottedTableHeader

export const SlottedTableHeader = (
  props: { doRender: boolean },
  children: VNode[]
) => {
  const headingClass = `fluid-table-header ${
    props.doRender ? 'visible' : 'hidden'
  }`;
  return <div class={headingClass}>{children}</div>;
};

// ================================================================ //
// -- CellPlaceholder

export const CellPlaceholder = (props: { conditionalOn }) =>
  props.conditionalOn ? <td class='table-data-cell' /> : '';

// ================================================================ //
// -- TableIconHeader

export interface TableIconHeaderProps {
  doRender: boolean;
  icon: any;
  headers: any[];
  columnAdjustment?: string;
  allowTabIndexCells?: boolean;
}

export const TableIconHeader = (props: TableIconHeaderProps) => {
  const { doRender, icon, headers, columnAdjustment, allowTabIndexCells } =
    props;
  return doRender
    ? [
        ...headers,
        <th
          class='table-header-cell action-column'
          style={{ width: columnAdjustment }}
          tabIndex={Utils.setTabIndex(allowTabIndexCells)}>
          <div class='header-cell-action icon-only'>{icon}</div>
        </th>,
      ]
    : headers;
};

// ================================================================ //
// -- TableActionCell (dedicated action column)

type TableActionCellProps = {
  identifier?: string;
  header: FluidTableHeader;
  dataItem: FluidRowData;
  actionClicked: EventEmitter<{ actionKey: string; rowData?: FluidRowData }>;
  disabled: boolean;
  theme: FluidTheme;
  buttonConfig?: (dataItem: FluidRowData) => FluidButtonConfig;
  handleCellClick: () => void;
};
export const TableActionCell = (props: TableActionCellProps) => {
  const { header, dataItem, actionClicked, disabled, theme, buttonConfig } =
    props;
  const { actionKey, icon, identifier, conditionalOn, dynamicIcon } =
    header.actionColumnConfig as FluidTableActionColumnConfig;
  const iconConfig =
    !!icon && typeof icon === 'string' ? { key: icon } : { ...icon };
  const dynamicIconConfig = !!dynamicIcon && dynamicIcon(dataItem);
  const dynamicButtonConfig: FluidButtonConfig =
    !!buttonConfig && buttonConfig(dataItem);
  const isDisabled =
    disabled ||
    iconConfig?.disabled ||
    dynamicIconConfig?.disabled ||
    dynamicButtonConfig?.disabled;
  const shouldRender: boolean = conditionalOn ? conditionalOn(dataItem) : true;
  const popoverConfig = extractIconPopoverConfig(
    dynamicIconConfig || iconConfig,
    { tagName: 'FLUID-TABLE' } as HTMLFluidTableElement
  );
  const rowId = dataItem?.rowId;

  const handleActionClick = () => {
    if (isDisabled) return;
    actionClicked.emit({ actionKey, rowData: dataItem });
  };

  if (!shouldRender || (!iconConfig && !dynamicIconConfig)) return;

  const iconButtonConfig = {
    icon: {
      size: themeVar<FluidSize>(theme, 'icons.default_size'),
      ...iconConfig, // Merge here to override "size" default (if necessary)
      ...(!!dynamicIconConfig && dynamicIconConfig),
      disabled: false, // False to prevent "double disabled states" (i.e. Icon AND Button)
    },
  };

  const actionProps = {
    disabled: isDisabled, // Button is disabled (instead of embedded Icon) so that Icon color is correct
    popoverConfig, // Implement Icon's Popover on parent Button (better usability)
  };

  return (
    <fluid-button
      id={identifier}
      data-test-id={`${rowId}-row-btn-${actionKey}`}
      onClick={() => handleActionClick()}
      config={
        buttonConfig
          ? {
              ...dynamicButtonConfig,
              ...actionProps,
              removeMargin: true,
            }
          : {
              ...iconButtonConfig,
              ...actionProps,
            }
      }
    />
  );
};

// ================================================================ //
// -- ColumnGroup

/**
 * Processes group headers and sets their visibility property to false
 * if the group they belong to has no data and all header labels are empty.
 */
export function setHeadersInvisibleForEmptyGroups(
  host,
  headers: FluidTableHeader[],
  columnGroups: FluidTableColumnGroup[]
): FluidTableHeader[] {
  const dataSource = host._dataSource;

  const isGroupDataEmpty = (group: FluidTableColumnGroup): boolean => {
    if (!Array.isArray(dataSource) || !dataSource.length) return true;
    const dataPaths = (group.headers || []).map((h) => h.dataPath);
    return dataSource.every((row) =>
      dataPaths.every((path) => row[path] == null || row[path] === '')
    );
  };

  return headers.map((header) => {
    const group = columnGroups.find(
      (g) => g.columnGroupId === header.columnGroupId
    );
    if (!group) return header;
    const groupHeaders = headers.filter(
      (h) => h.columnGroupId === group.columnGroupId
    );
    const isLabelEmpty = groupHeaders.every(
      (h) => !h.label || h.label.trim() === ''
    );
    if (isLabelEmpty && isGroupDataEmpty(group)) {
      return { ...header, visible: false };
    }
    return header;
  });
}

export const ColumnGroup = (props: {
  doRender: boolean;
  columnGroups: FluidTableColumnGroup[];
  headers: FluidTableHeader[];
}) => {
  const { columnGroups, headers, doRender } = props;
  const getColSpan = (headersForColumnGroup: FluidTableHeader[]) => {
    return hasSplitHeaders(headersForColumnGroup)
      ? headersForColumnGroup.reduce(
          (colSpan: number, current: FluidTableHeader) => {
            if (current.splitDataPoints?.length > 0) {
              colSpan += current.splitDataPoints.length;
            } else {
              colSpan += 1;
            }
            return colSpan;
          },
          0
        )
      : headersForColumnGroup.length;
  };
  return (
    doRender && (
      <tr class='table-header-colgroup-row'>
        {columnGroups.map((columnGroup: FluidTableColumnGroup, idx: number) => {
          const headersForColumnGroup = headers.filter(
            (h) => h.columnGroupId === columnGroup.columnGroupId && h.visible
          );
          const colSpan = getColSpan(headersForColumnGroup);
          const colgroupClass = {
            'table-header-colgroup-cell': true,
            'with-split-points': hasSplitHeaders(headers),
            [columnGroup.alignment]: true,
          };
          if (headersForColumnGroup.every((h) => h.visible === false))
            return null;
          return (
            <th
              colSpan={idx === columnGroups.length - 1 ? colSpan + 1 : colSpan}
              class={colgroupClass}>
              {columnGroup.template ? (
                <FluidComponentFromDefinition
                  definition={columnGroup.template}
                />
              ) : (
                columnGroup.label
              )}
            </th>
          );
        })}
      </tr>
    )
  );
};

// ================================================================ //
// -- TableHeaderCell

/**
 * Renders a table header cell.
 */
export const TableHeaderCell = (props: TableHeaderCellProps) => {
  const {
    header,
    headers,
    columnAdjustments,
    allowOverflow,
    actionSortBy,
    allowTabIndexCells,
    stickyColumn, // true IFF Prop "stickyColumn: true" AND column index is 0
    selectionOptions,
    host,
  } = props;
  const _hasSplitHeaders = hasSplitHeaders(headers);
  const _hasStickyColumn = !_hasSplitHeaders && !!stickyColumn;
  const cellAlignment = getCellAlignment(header, _hasSplitHeaders);
  const clickable = !header.disableSort && !header.actionColumnConfig;

  const skipTabIndexForActionColumn =
    allowTabIndexCells && !header.actionColumnConfig && !!header.disableFilters;

  const computeSortBy = async (event) => {
    const isShiftPressed = event.shiftKey;

    if (clickable) {
      switch (header.sortDirection) {
        case null:
        case undefined: {
          actionSortBy(header.dataPath, FluidSortOrder.ASC, isShiftPressed);
          break;
        }
        case FluidSortOrder.ASC: {
          actionSortBy(header.dataPath, FluidSortOrder.DESC, isShiftPressed);
          break;
        }
        case FluidSortOrder.DESC: {
          if (isShiftPressed) {
            actionSortBy(header.dataPath, FluidSortOrder.ASC, true);
          } else {
            await host.clearSort();
          }
          break;
        }
        default: {
          if (!isShiftPressed) {
            await host.clearSort();
          }
        }
      }
    }
  };

  // compute left offset for sticky column
  const _stickyColOffset = getSelectColumnWidth(
    selectionOptions,
    _hasStickyColumn,
    columnAdjustments?.selectable
  );

  const fixedWidthStyles = getFixedWidthStyle(
    header,
    headers,
    columnAdjustments,
    allowOverflow
  );

  const _styles = {
    ...fixedWidthStyles,
    ...(_hasStickyColumn && { left: _stickyColOffset || null }),
  };
  const processedHeaders = setHeadersInvisibleForEmptyGroups(
    host,
    host._headers,
    host.columnGroups
  );
  const processedHeader = processedHeaders.find(
    (h) =>
      h.dataPath === header.dataPath && h.columnGroupId === header.columnGroupId
  );

  const headerCellClass = {
    'table-header-cell': true,
    [cellAlignment]: true,
    hidden: processedHeader ? !processedHeader.visible : !header.visible,
    'editable-column': !!header.editableCellConfig,
    'action-column': !!header.actionColumnConfig,
    'sticky-column': _hasStickyColumn,
    'fixed-width': !Utils.isEmptyObject(fixedWidthStyles),
    'with-split-points': _hasSplitHeaders,
    'last-split': _hasSplitHeaders && props.splitLevel > 1,
  };

  return (
    <th
      id={TABLE_HEADER_CELL_ID(host.tableId, props.header.dataPath)}
      colSpan={props.colSpan}
      rowSpan={props.rowSpan}
      class={headerCellClass}
      tabIndex={Utils.setTabIndex(skipTabIndexForActionColumn)}
      style={_styles}
      onClick={(event) => computeSortBy(event)}>
      {header.actionColumnConfig ? (
        <TableHeaderAction {...props} />
      ) : (
        <TableHeaderDefault
          alignment={cellAlignment}
          {...props}
          fluidTranslate={host?.fluidTranslate}
        />
      )}
    </th>
  );
};

// ================================================================ //
// -- TableHeaderRow

export interface TableHeaderProps {
  host: FluidTable;
  headers: HTMLElement[];
  selectionOptions?: FluidTableSelectionOptions;
  allowTabIndexRows?: boolean;
  allowTabIndexCells?: boolean;
  i18nStore?: FluidI18nStore;
  identifier: string;
  tableDisplayStateStore: TableDisplayStateStore;
}

/**
 * Renders a default table head.
 */
export const TableHeaderRow = (props: TableHeaderProps) => {
  const {
    host,
    headers,
    selectionOptions,
    allowTabIndexRows,
    allowTabIndexCells,
    i18nStore,
    identifier,
    tableDisplayStateStore,
  } = props;

  const processedHeaders = setHeadersInvisibleForEmptyGroups(
    host,
    host._headers,
    host.columnGroups
  );
  return (
    !hasSplitHeaders(host._headers) && (
      <thead>
        <ColumnGroup
          doRender={host._canUseColumnGroups}
          columnGroups={host.columnGroups}
          headers={processedHeaders}
        />
        <tr
          class='table-header-row'
          tabIndex={Utils.setTabIndex(!!allowTabIndexRows)}>
          <AddNonDataHeaders
            headers={headers}
            host={host}
            selectionOptions={selectionOptions}
            allowTabIndexCells={!!allowTabIndexCells}
            tableDisplayStateStore={tableDisplayStateStore}
            i18nStore={i18nStore}
          />
          <ColumnFilter
            identifier={identifier}
            headers={host._headers}
            i18nStore={i18nStore}
            rowActions={host.rowActions}
            renderColumn={host._renderActionsColumn}
            renderFilter={!host.disableColumnVisibility}
            fluidTranslate={host.fluidTranslate}
            theme={host.theme}
            onColumnFiltersChanged={(columnState) =>
              handleColumnVisibilityChange(host, columnState)
            }
            buttonConfig={host.columnVisibilityButtonConfig}
          />
        </tr>
      </thead>
    )
  );
};

// ================================================================ //
// -- SplitPointHeader

/**
 * Renders a table head with split point headers.
 */
export const SplitPointHeader = (props: {
  host: FluidTable;
  topLevelHeaders: HTMLElement[];
  secondLevelHeaders: HTMLElement[];
  selectionOptions?: FluidTableSelectionOptions;
  identifier: string;
  tableDisplayStateStore: TableDisplayStateStore;
  i18nStore?: FluidI18nStore;
}) => {
  const {
    host,
    topLevelHeaders,
    secondLevelHeaders,
    selectionOptions,
    tableDisplayStateStore,
    i18nStore,
  } = props;

  return (
    hasSplitHeaders(host._headers) && (
      <thead>
        <ColumnGroup
          doRender={host._canUseColumnGroups}
          columnGroups={host.columnGroups}
          headers={host._headers}
        />
        <tr class='table-header-row'>
          <AddNonDataHeaders
            host={host}
            headers={topLevelHeaders}
            selectionOptions={selectionOptions}
            tableDisplayStateStore={tableDisplayStateStore}
            i18nStore={i18nStore}
          />
          <ColumnFilter
            headers={host._headers}
            rowActions={host.rowActions}
            rowSpan={2}
            splitHeaders={true}
            theme={host.theme}
            fluidTranslate={host.fluidTranslate}
            renderColumn={host._renderActionsColumn}
            renderFilter={!host.disableColumnVisibility}
            onColumnFiltersChanged={(columnState) =>
              handleColumnVisibilityChange(host, columnState)
            }
            buttonConfig={host.columnVisibilityButtonConfig}
          />
        </tr>
        <tr
          class={{
            'table-header-row': true,
            'no-action-column': host.disableColumnVisibility,
          }}>
          <AddNonDataHeaders
            host={host}
            headers={secondLevelHeaders}
            selectionOptions={selectionOptions}
            tableDisplayStateStore={tableDisplayStateStore}
            i18nStore={i18nStore}
          />
        </tr>
      </thead>
    )
  );
};

// ================================================================ //
// -- TableHeaderAction

/**
 * Renders a table action
 */
export const TableHeaderAction = (props: TableHeaderCellProps) => {
  const { header, theme, i18nStore } = props;
  let { icon } = header.actionColumnConfig;

  if (typeof icon === 'string') {
    Utils.deprecationWarning(
      { tagName: 'fluid-table' } as HTMLElement,
      'actionColumnConfig',
      'icon',
      'icon',
      'FluidIconConfig',
      'string'
    );
    icon = { key: icon };
  }

  return (
    <div
      class={{
        'header-cell-action': true,
        'icon-only': !header.label,
      }}>
      {header.label ? (
        <span class='header-cell-label-text'>
          {i18nStore?.translate(header.fluidTranslate?.label) || header.label}
        </span>
      ) : header.actionColumnConfig?.disableHeaderIcon ? null : (
        <fluid-icon
          class='header-icon'
          config={{
            size: themeVar<FluidSize>(theme, 'icons.default_size'),
            ...icon,
          }}
        />
      )}
    </div>
  );
};

// ================================================================ //
// -- TableCellSet

export interface UserDefinedTableColumnsProps {
  headers: FluidTableHeader[];
  dataItem: FluidRowData;
  host: any;
  isSubsetItem?: boolean;
  groupHeader: boolean;
  rowDisabled: boolean;
  allowTabIndexCells: boolean;
  selectionOptions: FluidTableSelectionOptions;
  rowId: string;
  identifier: string;
}

export const TableCellSet = (props: UserDefinedTableColumnsProps) => {
  let { headers } = props;
  const {
    dataItem,
    host,
    isSubsetItem,
    groupHeader,
    rowDisabled,
    allowTabIndexCells,
    selectionOptions,
    rowId,
    identifier,
  } = props;

  const _hasSplitHeaders = hasSplitHeaders(headers);
  const _hasStickyColumn = !_hasSplitHeaders && !!host._stickyColumn;

  if (_hasSplitHeaders) {
    headers = headers.reduce(
      (newHeaders: FluidTableHeader[], current: FluidTableHeader) => {
        if (current.splitDataPoints) {
          newHeaders.push(...current.splitDataPoints);
        } else {
          newHeaders.push(current);
        }
        return newHeaders;
      },
      []
    );
  }

  return headers.map((header: FluidTableHeader, index) => {
    const skipTabIndexForEditableCell =
      allowTabIndexCells && !header.editableCellConfig;

    const dataCellClass = {
      'table-data-cell': true,
      [getCellAlignment(header)]: true,
      hidden: !header.visible,
      'action-cell': !!header.actionColumnConfig,
      'sticky-column': _hasStickyColumn && index === 0,
      highlight:
        typeof header.highlight === 'function'
          ? header.highlight(dataItem)
          : header.highlight,
    };

    // compute left position for sticky column
    const _stickyColOffset =
      index === 0 &&
      getSelectColumnWidth(
        selectionOptions,
        _hasStickyColumn,
        host.columnAdjustments?.selectable
      );

    const _styles = {
      ...(!!header.editableCellConfig &&
        Utils.browserIsMsie() && {
          verticalAlign: 'top',
        }),
      ...(_hasStickyColumn && { left: _stickyColOffset || null }),
    };

    return (
      <td
        class={dataCellClass}
        id={TABLE_CELL_ID(header.dataPath, rowId)}
        tabIndex={Utils.setTabIndex(skipTabIndexForEditableCell)}
        onClick={() => handleCellClick(host, dataItem, header)}
        style={_styles}>
        <TableCell
          identifier={identifier}
          disabledRow={rowDisabled}
          isSubsetCell={isSubsetItem}
          host={host}
          header={header}
          dataItem={dataItem}
          groupHeader={groupHeader}
        />
      </td>
    );
  });
};

// ================================================================ //
// -- TableHeaderDefault & Children

/**
 * Renders a default table header with label and sort/filter icons
 */
export const TableHeaderDefault = (props: TableHeaderCellProps) => {
  const { header, theme, alignment } = props;
  return (
    <div class={`header-cell-content align-${alignment}`}>
      <TableHeaderLabel {...props} />
      <SortIcon
        doRender={header.sorted}
        sortDirection={header.sortDirection}
        theme={theme}
      />
      <Filter {...props} stopPropagation />
    </div>
  );
};

/**
 * Renders a Table header cell's label & optional "helptext" Popover.
 */
export const TableHeaderLabel = (props: TableHeaderCellProps) => {
  const { header, i18nStore } = props;
  return (
    <div class='header-cell-label'>
      <span
        class='header-cell-label-text'
        innerHTML={
          i18nStore?.translate(header.fluidTranslate?.label) || header.label
        }
      />
      <TableHeaderHelpText {...props} />
    </div>
  );
};

/**
 * Renders a Table header cell's "helptext" Popover (if one is defined).
 */
export const TableHeaderHelpText = (props: TableHeaderCellProps) => {
  const { theme, header, host, i18nStore } = props;

  return (
    !!(
      header.helpText || i18nStore?.translate(header.fluidTranslate?.helpText)
    ) && (
      <InfoPopover
        helpText={
          i18nStore?.translate(header.fluidTranslate?.helpText) ||
          header.helpText
        }
        theme={theme}
        stopPropagation={true}
        ignoreClippingBoundaries={stickyClipFix(host)}
      />
    )
  );
};

// ================================================================ //
// -- AddNonDataHeaders

export interface ApplyHeadersProps {
  host: FluidTable;
  headers: HTMLElement[];
  selectionOptions: FluidTableSelectionOptions;
  allowTabIndexCells?: boolean;
  tableDisplayStateStore: TableDisplayStateStore;
  i18nStore?: FluidI18nStore;
}

export const AddNonDataHeaders = (props: ApplyHeadersProps) => {
  const {
    host,
    headers,
    selectionOptions,
    allowTabIndexCells,
    tableDisplayStateStore,
    i18nStore,
  } = props;
  return AddExpandAllFormsHeader(
    host,
    AddExpandGroupsHeader(
      host,
      AddSelectHeader(
        host,
        selectionOptions,
        <TableIconHeader
          allowTabIndexCells={!!allowTabIndexCells}
          doRender={host.handleDelete}
          columnAdjustment={host.columnAdjustments?.delete}
          icon={
            <DeleteIcon
              doRender={host.handleDelete}
              handleDelete={() => host.deleteAll()}
              disabled={host.enableDeleteAll && host._tableDisabled}
              theme={host.theme}
              class={{
                'header-icon': true,
                'non-interactive': !host.enableDeleteAll,
              }}
            />
          }
          headers={AddExpandRowTemplatesColumn(
            host,
            headers,
            tableDisplayStateStore
          )}
        />,
        i18nStore
      )
    )
  );
};

// ================================================================ //
// -- AddExpandGroupsHeader

export const AddExpandGroupsHeader = (host, headers) => {
  const { expandedButtonPosition, hideExpandAll, tableDisplayStateStore } =
    host;
  const allExpanded = host._groupedDataSource.every(
    (ds: FluidTableGroupedDataset) => {
      return host.tableDisplayStateStore.isGroupExpanded(
        ds.groupHeader?.groupId
      );
    }
  );
  const toggle = () => {
    host._groupedDataSource.forEach((item) => {
      tableDisplayStateStore.toggleGroupExpanded(
        item.groupHeader.groupId,
        !allExpanded
      );
      host.updateGroupDataSource(item.groupHeader.groupId, {
        [GROUP_EXPANDED]: !allExpanded,
      });
      host.groupsToggled.emit(collectGroupCollapsedState(host));
    });
  };

  const icon = (
    <th
      role='button'
      aria-label='Expand All Groups'
      tabIndex={0}
      onClick={() => !hideExpandAll && toggle()}
      onKeyDown={(e) => !hideExpandAll && EventUtils.keySelect(e)}
      class={{
        'table-header-cell expand-trigger-column': true,
        'with-split-points': hasSplitHeaders(host._headers),
      }}>
      {!hideExpandAll && (
        <fluid-icon
          iconKey={getChevronIcon(allExpanded)}
          class={'header-icon'}
          theme={host.theme}
          size={FluidSize.SMALL}
          disabled={hideExpandAll}
        />
      )}
    </th>
  );

  // For AddExpandGroupsHeader the default for the expanded button is to the left
  const sortedHeaders = sortRowsBasedOnExpandButtonPosition(
    expandedButtonPosition,
    icon,
    headers,
    [icon, ...headers]
  );
  return isGroupedTable(host._headers) &&
    !isAdditionalHeaderTable(host._headers)
    ? sortedHeaders
    : headers;
};

// ================================================================ //
// -- AddExpandRowTemplatesColumn

/**
 * Renders header cell Icon Toggle for expandableRowTemplate.
 * @param host
 * @param headers
 * @constructor
 */
export const AddExpandRowTemplatesColumn = (
  host,
  headers,
  tableDisplayStateStore
) => {
  const {
    expandableRowTemplate: hasCellTemplates,
    expandedButtonPosition,
    rowSubset,
    hideExpandAll,
  } = host;

  const allExpanded = host._dataSource.every((ds) => ds[EXPANDED]);
  const toggle = () => {
    host._dataSource.forEach((item) => {
      tableDisplayStateStore.toggleRowExpanded(item.rowId, !allExpanded);
      host.updateDataSource(item.rowId, {
        [EXPANDED]: !allExpanded,
      });
    });
  };

  const icon = (
    <th
      role='button'
      aria-label='Expand All Rows'
      tabIndex={0}
      onClick={() => !hideExpandAll && toggle()}
      onKeyDown={(e) => !hideExpandAll && EventUtils.keySelect(e)}
      class={{
        'table-header-cell expand-trigger-column': true,
        'with-split-points': hasSplitHeaders(host._headers),
      }}>
      {!hideExpandAll && (
        <fluid-icon
          iconKey={getChevronIcon(allExpanded)}
          class={'header-icon'}
          theme={host.theme}
          size={FluidSize.SMALL}
          disabled={hideExpandAll}
        />
      )}
    </th>
  );

  // For AddExpandRowTemplatesColumn the default for the expanded button is to the right
  const sortedHeaders = sortRowsBasedOnExpandButtonPosition(
    expandedButtonPosition,
    icon,
    headers,
    [...headers, icon]
  );
  return hasCellTemplates || rowSubset ? sortedHeaders : headers;
};

// ================================================================ //
// -- AddExpandAllFormsHeader

export const AddExpandAllFormsHeader = (host, headers) => {
  const { expandedButtonPosition } = host;
  const allExpanded = host._dataSource.every((ds) => ds[FORM_EXPANDED]);
  const toggle = () => {
    Object.keys(host._expandedForms).forEach(
      (rowId) => (host._expandedForms[rowId] = !allExpanded)
    );
    host._dataSource = host._dataSource.map((ds) => ({
      ...ds,
      [FORM_EXPANDED]: !allExpanded,
    }));
  };

  const icon = (
    <th
      role='button'
      aria-label='Expand All Forms'
      tabIndex={0}
      onClick={() => toggle()}
      onKeyDown={(e) => EventUtils.keySelect(e)}
      class={{
        'table-header-cell expand-trigger-column': true,
        'with-split-points': hasSplitHeaders(host._headers),
      }}>
      <fluid-icon
        iconKey={getChevronIcon(allExpanded)}
        class={'header-icon'}
        theme={host.theme}
        size={FluidSize.SMALL}
      />
    </th>
  );

  // For AddExpandAllFormsHeader the default for the expanded button is to the left
  const sortedHeaders = sortRowsBasedOnExpandButtonPosition(
    expandedButtonPosition,
    icon,
    headers,
    [icon, ...headers]
  );
  return host.expandableForm ? sortedHeaders : headers;
};

// ================================================================ //
// -- Functional Component Utilities

/**
 * Returns true if any of the headers have split data points.
 */
export const hasSplitHeaders = (headers: FluidTableHeader[]) =>
  headers.filter((h) => !!h.splitDataPoints).length > 0;

/**
 * Returns the appropriate chevron Icon showing whether expandable
 * Table rows are all expanded or collapsed.
 */
const getChevronIcon = (allExpanded: boolean) => {
  return allExpanded ? FluidIconKey.CHEVRON_UP : FluidIconKey.CHEVRON_DOWN;
};

/**
 * Currently, a bug exists in Firefox & Safari that causes elements with
 * "position: fixed" to be clipped when they overflow the boundaries
 * of a parent element with "position: sticky" when an ancestor
 * element has "overflow-y" styles set. This means that "helpText" Popovers
 * within Table header cells are clipped when "stickyHeaders" are enabled.
 * This utility function determines if the consuming browser is Firefox
 * & if Table has "stickyHeaders" enabled - if so, returns the appropriate
 * value for Popover's "ignoreClippingBoundaries" Prop so that the clipping
 * issue is fixed.
 */
const stickyClipFix = (host) => {
  const userAgentString = navigator.userAgent.toLowerCase();
  const isChrome = userAgentString.includes('chrome');
  const isSafari = !isChrome && userAgentString.includes('safari');
  const isFirefox = userAgentString.includes('firefox');
  // If conditions are hit, return FALSE for "ignoreClippingBoundaries" Prop value
  return !((isFirefox || isSafari) && !!host.stickyHeaders && !!host.maxHeight);
};
